// src/components/tables/shared/PharmacyDataGrid.jsx
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  createTheme,
  ThemeProvider,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ShoppingCart,
  TrendingUp,
  MedicalServices,
  Search,
} from "@mui/icons-material";
import { DOCLINK_COLORS, PHARMACY_HALO_COLORS, QUDA_COLORS } from "@/config/colors";
import { dataGridCellNoFocus } from "@/utils/adminTableStyles";
import { DocLinkBoxTheme, QudaBoxTheme } from "@/config/boxThemes";
import StatBox from "@/components/data-visuals/Statbox";
import QudaLogo from "@/Images/Logos/Quda_Logo.png";
import DocLinkLogo from "@/Images/Logos/DocLink_Logo.png";
import { getChemistName } from "@/utils/textUtils";

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const formatPharmacyRow = (pharmacy = {}, index) => {
  const {
    chemistID,
    pharmacyName,
    staffCount,
    terminalCount,
    qudaStats = {},
    waitingListStats = {},
    doclinkStats = {},
  } = pharmacy;

  const { totalOrdersEver, ordersLast30Days, ordersPrevious30Days } = qudaStats;

  const { activeWaitingListRecords, waitingListLast30Days } = waitingListStats;

  const {
    totalConsultsCreated,
    appointmentsLast30Days,
    appointmentsPrevious30Days,
    consultsLast30Days,
    consultsPrevious30Days,
    weeklyAverageLast4Weeks,
  } = doclinkStats;

  return {
    id: index + 1,
    chemistID: chemistID,
    pharmacyName: pharmacyName,
    staffCount: toNumber(staffCount),
    terminalCount: toNumber(terminalCount),
    totalOrdersEver: toNumber(totalOrdersEver),
    ordersLast30Days: toNumber(ordersLast30Days),
    ordersPrevious30Days: toNumber(ordersPrevious30Days),
    activeWaitingListRecords: toNumber(activeWaitingListRecords),
    waitingListLast30Days: toNumber(waitingListLast30Days),
    totalConsultsCreated: toNumber(totalConsultsCreated),
    appointmentsLast30Days: toNumber(
      appointmentsLast30Days ?? consultsLast30Days
    ),
    appointmentsPrevious30Days: toNumber(
      appointmentsPrevious30Days ?? consultsPrevious30Days
    ),
    weeklyAverageLast4Weeks: toNumber(weeklyAverageLast4Weeks),
  };
};

const PharmacyDataGrid = ({ data, isSubscriptionMode = false, onPingAppTeam }) => {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState("");

  const centeredColumnProps = {
    headerAlign: "center",
    align: "center",
  };

  const transformedData = useMemo(() => {
    if (isSubscriptionMode) {
      return (data?.MainData?.pharmacyData ?? []).map((item, index) => ({
        id: index + 1,
        chemistID: item.chemistID,
        pharmacyName: item.pharmacyName,
        QudaSubStatus: item.QudaSubStatus,
        DocLinkSubStatus: item.DocLinkSubStatus,
      }));
    }
    return (data?.MainData?.pharmacyData ?? []).map(formatPharmacyRow);
  }, [data, isSubscriptionMode]);

  const summaryStats = useMemo(() => {
    if (isSubscriptionMode) return null;

    const totalOrders = transformedData.reduce(
      (s, it) => s + it.totalOrdersEver,
      0
    );
    const totalOrders30Days = transformedData.reduce(
      (s, it) => s + it.ordersLast30Days,
      0
    );
    const totalOrdersPrevious30Days = transformedData.reduce(
      (s, it) => s + it.ordersPrevious30Days,
      0
    );
    const totalAppointments = transformedData.reduce(
      (s, it) => s + it.totalConsultsCreated,
      0
    );
    const totalAppointments30Days = transformedData.reduce(
      (s, it) => s + it.appointmentsLast30Days,
      0
    );
    const totalAppointmentsPrevious30Days = transformedData.reduce(
      (s, it) => s + it.appointmentsPrevious30Days,
      0
    );
    const appointments30DayDelta =
      totalAppointments30Days - totalAppointmentsPrevious30Days;
    const orders30DayDelta = totalOrders30Days - totalOrdersPrevious30Days;
    return {
      totalOrders,
      totalOrders30Days,
      totalOrdersPrevious30Days,
      orders30DayDelta,
      totalAppointments,
      totalAppointments30Days,
      totalAppointmentsPrevious30Days,
      appointments30DayDelta,
    };
  }, [transformedData, isSubscriptionMode]);

  const filteredData = useMemo(() => {
    if (!filterText) return transformedData;
    return transformedData.filter((it) =>
      it.pharmacyName.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [transformedData, filterText]);

  const getStatusStyle = (status) => {
    if (!status) return {};
    const normalizedStatus = status.toString().toLowerCase();
    if (normalizedStatus === "active") {
      return { color: "green", fontWeight: "bold" };
    } else if (normalizedStatus === "cancelled") {
      return { color: "red", fontWeight: "bold" };
    } else if (normalizedStatus.includes("trial")) {
      // Includes 'trialing' or 'trial'
      return { color: "blue", fontWeight: "bold" };
    }
    return {};
  };

  const renderStatusCell = (params, appName) => {
    if (!params.value && isSubscriptionMode && onPingAppTeam) {
      const isQuda = appName === "Quda";
      const btnBg = isQuda ? QUDA_COLORS.TEAL_DARK : DOCLINK_COLORS.MID_PURPLE;
      const btnHover = isQuda ? QUDA_COLORS.BURGUNDY : DOCLINK_COLORS.DARK_PURPLE;

      return (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onPingAppTeam(params.row, appName);
          }}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            boxShadow: 1,
            backgroundColor: btnBg,
            minWidth: 68,
            height: 32,
            lineHeight: 1,
            padding: "0 12px",
            "&:hover": {
              boxShadow: 3,
              backgroundColor: btnHover,
            },
          }}
        >
          Activate
        </Button>
      );
    }

    const style = getStatusStyle(params.value);
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        {params.value || "-"}
      </div>
    );
  };

  const columns = useMemo(() => {
    if (isSubscriptionMode) {
      return [
        {
          field: "pharmacyName",
          headerName: "Pharmacy Name",
          flex: 1,
          minWidth: 200,
          ...centeredColumnProps,
          renderCell: (params) => getChemistName(params.value),
        },
        {
          field: "QudaSubStatus",
          headerName: "Quda Status",
          width: 150,
          ...centeredColumnProps,
          renderCell: (params) => renderStatusCell(params, "Quda"),
        },
        {
          field: "DocLinkSubStatus",
          headerName: "DocLink Status",
          width: 150,
          ...centeredColumnProps,
          renderCell: (params) => renderStatusCell(params, "DocLink"),
        },
        {
          field: "actions",
          headerName: "Actions",
          width: 150,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          ...centeredColumnProps,
          renderCell: (params) => (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate(`/chemist/${params.row.chemistID}`)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  boxShadow: 1,
                  backgroundColor: PHARMACY_HALO_COLORS.BLUE,
                  minWidth: 68,
                  height: 32,
                  lineHeight: 1,
                  padding: "0 12px",
                  "&:hover": {
                    boxShadow: 3,
                    backgroundColor: PHARMACY_HALO_COLORS.RED,
                  },
                }}
              >
                Open
              </Button>
            </div>
          ),
        },
      ];
    }

    return [
      {
        field: "pharmacyName",
        headerName: "Pharmacy Name",
        flex: 1,
        minWidth: 200,
        ...centeredColumnProps,
        renderCell: (params) => getChemistName(params.value),
      },
      {
        field: "staffCount",
        headerName: "Registered Staff",
        width: 120,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "terminalCount",
        headerName: "Installed Computers",
        width: 140,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "totalOrdersEver",
        headerName: "Total Orders",
        width: 130,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "ordersLast30Days",
        headerName: "Orders (30d)",
        width: 130,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "totalConsultsCreated",
        headerName: "Appointments",
        width: 130,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "weeklyAverageLast4Weeks",
        headerName: "Weekly Ave (4wks)",
        width: 160,
        type: "number",
        ...centeredColumnProps,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        ...centeredColumnProps,
        renderCell: (params) => (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/chemist/${params.row.chemistID}`)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: 1,
                backgroundColor: PHARMACY_HALO_COLORS.BLUE,
                minWidth: 68,
                height: 32,
                lineHeight: 1,
                padding: "0 12px",
                "&:hover": {
                  boxShadow: 3,
                  backgroundColor: PHARMACY_HALO_COLORS.RED,
                },
              }}
            >
              Open
            </Button>
          </div>
        ),
      },
    ];
  }, [isSubscriptionMode, navigate, onPingAppTeam]);

  const theme = createTheme({
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: "white",
            borderRadius: 12,
            border: `2px solid ${PHARMACY_HALO_COLORS.BLUE}`,
          },
          columnHeaders: {
            backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
            color: "#FFF !important",
            borderBottom: "none",
          },
          columnHeader: {
            backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
            color: "#FFF !important",
            "&:focus": { outline: "none" },
          },
          footerContainer: {
            backgroundColor: "#fff",
            borderTop: `2px solid ${PHARMACY_HALO_COLORS.RED}`,
          },
        },
      },
    },
    typography: { fontSize: 14 },
  });

  return (
    <ThemeProvider theme={theme}>
      <div className="space-y-6">
        {/* Summary Stats (now using one reusable component) */}
        {!isSubscriptionMode && summaryStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatBox
              theme={QudaBoxTheme}
              logo={QudaLogo}
              title="Total Orders"
              value={summaryStats.totalOrders}
              Icon={ShoppingCart}
              valueFormatter={(v) => Number(v).toLocaleString()}
            />
            <StatBox
              theme={QudaBoxTheme}
              logo={QudaLogo}
              title="Orders (30d)"
              value={summaryStats.totalOrders30Days}
              Icon={TrendingUp}
              valueFormatter={(v) => Number(v).toLocaleString()}
              deltaValue={
                summaryStats.totalOrdersPrevious30Days > 0
                  ? (summaryStats.orders30DayDelta /
                      summaryStats.totalOrdersPrevious30Days) *
                    100
                  : 0
              }
              deltaFormatter={(v) =>
                `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`
              }
              deltaLabel="Orders"
            />
            <StatBox
              theme={DocLinkBoxTheme}
              logo={DocLinkLogo}
              title="Total Appointments"
              value={summaryStats.totalAppointments}
              Icon={MedicalServices}
              valueFormatter={(v) => Number(v).toLocaleString()}
            />
            <StatBox
              theme={DocLinkBoxTheme}
              logo={DocLinkLogo}
              title="Appointments (30d)"
              value={summaryStats.totalAppointments30Days}
              Icon={TrendingUp}
              valueFormatter={(v) => Number(v).toLocaleString()}
              deltaValue={
                summaryStats.totalAppointmentsPrevious30Days > 0
                  ? (summaryStats.appointments30DayDelta /
                      summaryStats.totalAppointmentsPrevious30Days) *
                    100
                  : 0
              }
              deltaFormatter={(v) =>
                `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`
              }
              deltaLabel="Appointments"
              deltaPositiveColor="#4ade80"
              deltaNegativeColor="#dc2626"
              deltaNeutralColor="#4ade80"
            />
          </div>
        )}

        {/* Data Grid */}
        <div
          className="bg-white rounded-lg shadow-lg border-2 overflow-hidden"
          style={{ borderColor: PHARMACY_HALO_COLORS.BLUE, borderWidth: "3px" }}
        >
          <div className="p-4" style={{ width: "100%" }}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6 mb-4">
              <div className="w-full md:max-w-xs">
                <TextField
                  fullWidth
                  placeholder="Search by pharmacy name..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: !filterText && (
                      <InputAdornment position="start">
                        <Search
                          sx={{
                            color: PHARMACY_HALO_COLORS.BLUE,
                            opacity: 0.7,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: PHARMACY_HALO_COLORS.BLUE,
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: PHARMACY_HALO_COLORS.RED,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: PHARMACY_HALO_COLORS.YELLOW,
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: PHARMACY_HALO_COLORS.BLUE,
                      opacity: 0.7,
                    },
                  }}
                />
              </div>
              <div className="flex-1 text-center">
                <h3
                  className="text-xl font-bold"
                  style={{ color: PHARMACY_HALO_COLORS.BLUE }}
                >
                  {isSubscriptionMode ? "Subscriptions" : "Pharmacy Data"}
                </h3>
              </div>
              <div className="hidden md:block w-full md:max-w-xs" />
            </div>

            <div style={{ width: "100%", height: 520 }}>
              <DataGrid
                rows={filteredData}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                disableColumnReorder
                disableColumnResize
                disableMultipleSelection
                disableSelectionOnClick
                disableRowSelection
                disableVirtualization
                disableIgnoreModificationsIfProcessingProps
                pagination
                initialState={{
                  pagination: { paginationModel: { page: 0, pageSize: 50 } },
                }}
                pageSizeOptions={[20, 50, 100]}
                density="standard"
                rowHeight={52}
                headerHeight={80}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #e5e7eb",
                    fontSize: 14,
                    cursor: "default",
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  },
                  ...dataGridCellNoFocus,
                  "& .MuiDataGrid-cellContent": {
                    width: "100%",
                    textAlign: "center",
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "default",
                    userSelect: "none",
                    "&:hover": {
                      backgroundColor: `${PHARMACY_HALO_COLORS.YELLOW} !important`,
                      opacity: 0.85,
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: PHARMACY_HALO_COLORS.LIGHT_GRAY,
                    },
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
                    color: "#FFF !important",
                    "& .MuiDataGrid-columnSeparator": {
                      color: "#FFF",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      opacity: 1,
                      color: "#FFF !important",
                      display: isSubscriptionMode ? "none" : "block",
                    },
                    "& .MuiDataGrid-menuIconButton": {
                      color: "#FFF !important",
                    },
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "#FFF !important",
                    fontWeight: "bold",
                    whiteSpace: "normal",
                    lineHeight: "1.2",
                    textAlign: "center",
                    padding: "4px 8px",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    cursor: "default",
                    userSelect: "none",
                    height: "auto !important",
                    minHeight: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default PharmacyDataGrid;
